import React from 'react';

import { Button, Form, Layout, message, Row, Skeleton, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { CreateProductSchema } from '@app/models/products';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { HeaderTitle } from '@components/HeaderTitle';
import { ProductForm } from '@components/products';
import { useGetProductQuery, useUpdateProductQuery } from '@pages/Products/hooks';

import { useGetCatalogQuery } from '../../../../hooks';

export const EditProduct: React.FC = () => {
  const params = useParams<{ id: string; productId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm<CreateProductSchema>();

  const commonT = useTranslation(NSKeys.common);
  const nomenclatureEditT = useTranslation(NSKeys.nomenclatureEdit);
  const { data: catalog, isLoading: isCatalogLoading } = useGetCatalogQuery(params.id || '');
  const { data: product, isLoading: isProductLoading, status: getStatus } = useGetProductQuery(params?.productId || '');
  const { mutate: updateProduct, status: updateStatus } = useUpdateProductQuery();

  const goBack = (): void => {
    navigate(`/dashboard/catalogs/${params.id}`);
  };

  React.useEffect(() => {
    if (getStatus === 'success') {
      form.setFieldsValue({
        ...product,
        title: product?.title || '',
        code: product?.code || '',
        tags_ids: product?.tags.map((el) => el.id),
        category_id: product?.category?.id,
        brand_id: product?.brand?.id,
        image_uids: product?.images.map(
          (el) =>
            ({
              uid: el.uid,
              status: 'done',
              url: el.variants.thumbnail?.url,
            } as any)
        ),
      });
    }
    if (getStatus === 'error') {
      message.error(commonT.t('dataNotFound'));
      goBack();
    }
  }, [getStatus]);

  React.useEffect(() => {
    if (updateStatus === 'success') {
      goBack();
    }
  }, [updateStatus]);

  const onFinish = async (formValues: CreateProductSchema): Promise<void> => {
    const image_uids = formValues.image_uids?.map((el: any) => (el?.url ? el.uid : el?.response?.uid));
    updateProduct({ data: { ...formValues, image_uids }, id: params.productId || '' });
  };

  return (
    <Layout>
      <Layout.Header>
        <Breadcrumbs
          apiTitles={[
            { id: ':catalogId', value: catalog?.name },
            { id: ':productId', value: product?.title },
          ]}
        />
        <Row justify="space-between" align="middle">
          <HeaderTitle title={nomenclatureEditT.t('title')} loading={isCatalogLoading || isProductLoading} />
          <Space size="middle">
            <Button onClick={goBack} size="middle">
              {commonT.t('buttonCancel')}
            </Button>
            <Button
              disabled={isProductLoading || updateStatus === 'loading'}
              type="primary"
              loading={updateStatus === 'loading'}
              onClick={() => form.submit()}
              size="middle">
              {nomenclatureEditT.t('titleSave')}
            </Button>
          </Space>
        </Row>
      </Layout.Header>
      <Layout.Content style={{ padding: 24 }}>
        {isProductLoading || !product ? (
          <Skeleton active={true} />
        ) : (
          <ProductForm
            form={form}
            onFinish={onFinish}
            submitting={updateStatus === 'loading'}
            isPackageAmountEditable={product?.is_package_amount_editable}
          />
        )}
      </Layout.Content>
    </Layout>
  );
};
